import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Header } from '../../components/shared/header/header';
import { LeadsTable, LeadColumn, LeadRow, FilterConfig, ServerSidePagination } from '../../components/shared/leads-table/leads-table';
import { LeadsFilaService } from '../../services/leads-fila.service';
import { LeadsFilaRequest, LeadsFilaItem } from '../../interfaces/leads-fila.interface';
import { catchError } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { SelectedDnService } from '../../services/selected-dn.service';
import { FilterFieldsUtil } from '../../utils/filter-fields-utils';
import { availableOriginsType } from '../../components/shared/filter-fields/models/filter-field.model';
import { DashboardStoreService } from '../../services/store/dashboard.store.service';

@Component({
  selector: 'app-simulation-info',
  standalone: true,
  imports: [CommonModule, RouterModule, Header, LeadsTable],
  templateUrl: './simulation-info.html',
  styleUrls: ['./simulation-info.scss']
})
export class SimulationInfo implements OnInit, OnDestroy, AfterViewInit {
  private dnSubscription?: Subscription;
  private hasLoadedInitialData = false;
  private hasReceivedInitialDates = false;
  private isLoading = false;
  private initialLoadDone = false;
  availableFilterOrigins: availableOriginsType[] = ['sempre-novo', 'ofertas-vw', 'campanha'];

  @ViewChild(LeadsTable) leadsTable?: LeadsTable;

  tableColumns: LeadColumn[] = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'origem', label: 'Origem', type: 'text' },
    { key: 'dataSimulacao', label: 'Data de Simulação', type: 'text' },
    { key: 'ultimoContato', label: 'Último Contato', type: 'text' },
    { key: 'retornarEm', label: 'Retornar Em', type: 'text' },
    { key: 'status', label: 'Status do Lead', type: 'status' }
  ];

  filterConfig: FilterConfig = {
    searchFields: ['nome'],
    alphabeticalField: 'NM_NOME',
    filterFields: {
      renewal: 'renovaMais',
      preAprovado: 'preAprovacao',
      origin: 'origem'
    },
    dateField: 'dataSimulacao'
  };

  tableData: LeadRow[] = [];
  loading: boolean = false;
  loadingMessage: string = 'Carregando dados...';
  errorMessage: string = '';
  emptyMessage: string = 'Nenhum lead encontrado para o tipo de campanha informada.';
  serverSidePagination?: ServerSidePagination;

  private readonly tipoCampanha = 1;

  private currentSearchText: string | undefined = undefined;
  private currentFilters: string[] = [];
  private currentDateRange: { start: string, end: string } = { start: '', end: '' };
  private currentSortField: string = '';
  private currentSortOrder?: 'ASC' | 'DESC' = 'DESC';

  constructor(
    private leadsFilaService: LeadsFilaService,
    private readonly dashboardStoreService: DashboardStoreService,
    private router: Router,
    private selectedDnService: SelectedDnService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    this.currentDateRange = {
      start: this.formatDateForInput(threeMonthsAgo),
      end: this.formatDateForInput(today)
    };

    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (dn && this.hasReceivedInitialDates && this.initialLoadDone) {
        this.loadData(1, this.serverSidePagination?.pageSize || 20);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.hasLoadedInitialData && !this.hasReceivedInitialDates && !this.isLoading) {
        this.hasReceivedInitialDates = true;
        this.loadData();
      }
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  loadData(
    page: number = 1,
    pageSize: number = 20
  ): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.loading = true;
    this.loadingMessage = 'Carregando simulações...';
    this.errorMessage = '';
    this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';

    const request: LeadsFilaRequest = {
      tipoCampanha: this.tipoCampanha,
      dn: this.selectedDnService.getSelectedDnAsNumber(),
      preAprovado: FilterFieldsUtil.getPreAprovadoFromFilters(this.currentFilters),
      renovaMais: FilterFieldsUtil.getRenovaMaisFromFilters(this.currentFilters),
      origem: FilterFieldsUtil.getOrigemFromFilters(this.currentFilters),
      nome: this.currentSearchText,
      dataInicio: this.parseDate(this.dashboardStoreService.startDate()),
      dataFim: this.parseDate(this.dashboardStoreService.endDate()),
      page: page,
      pageSize: pageSize,
      sortField: this.currentSortField,
      sortOrder: this.currentSortOrder
    };

    this.leadsFilaService.getLeadsFila(request).pipe(
      catchError((error) => {
        this.isLoading = false;
        this.loading = false;

        if (error.status === 401) {
          this.errorMessage = 'Não autorizado. Redirecionando...';
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 2000);
        } else if (error.status === 204) {
          this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';
          this.tableData = [];
        } else {
          this.errorMessage = 'Erro ao carregar dados. Tente novamente.';
        }

        return of(null);
      })
    ).subscribe((response) => {
      this.isLoading = false;
      this.loading = false;

      if (response && response.data && response.data.length > 0) {
        this.tableData = response.data.map((item) => this.createBasicRow(item));
        this.serverSidePagination = {
          totalRecords: response.pagination.totalRecords,
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          from: response.pagination.from,
          to: response.pagination.to
        };
        this.hasLoadedInitialData = true;
        this.initialLoadDone = true;
      } else {
        this.emptyMessage = response?.message || 'Nenhum lead encontrado para o tipo de campanha informada.';
        this.tableData = [];
        this.hasLoadedInitialData = true;
        this.initialLoadDone = true;
      }
    });
  }

  onDateRangeChange(dateRange: { start: string, end: string }): void {
    if (this.currentDateRange.start === dateRange.start &&
      this.currentDateRange.end === dateRange.end) {
      return;
    }

    this.hasReceivedInitialDates = true;

    if (dateRange.start && dateRange.end) {
      this.currentDateRange = dateRange;
    } else {
      this.currentDateRange = dateRange;
    }

    this.loadData();
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private createBasicRow(item: LeadsFilaItem): LeadRow {
    return {
      idLead: item.idLead,
      nome: item.nomeCliente || '-',
      origem: item.origem || '-',
      dataSimulacao: item.dataSimulacao || '-',
      ultimoContato: (item.ultimoContato && item.ultimoContato !== 'null' && item.ultimoContato !== 'undefined') ? item.ultimoContato : '-',
      retornarEm: (item.retornarEm && item.retornarEm !== 'null' && item.retornarEm !== 'undefined') ? item.retornarEm : '-',
      status: item.statusLead || '-',
      renewal: this.mapRenovaMais(item),
      preAprovacao: this.mapPreAprovado(item)
    };
  }

  private mapPreAprovado(item: LeadsFilaItem): string {
    if (item.preAprovado === 'S') {
      return 'Sim';
    } else if (item.preAprovado === 'N') {
      return 'Necessita Análise';
    } else {
      return 'Não Solicitado';
    }
  }

  private mapRenovaMais(item: LeadsFilaItem): string {
    const flagRenovacao = this.obterFlagRenovacao(item);
    const isRenovaMais = flagRenovacao !== null ? flagRenovacao : item.renovaMais === true;
    return isRenovaMais ? 'renewalBlue' : 'renewalGrey';
  }

  private obterFlagRenovacao(item: LeadsFilaItem): boolean | null {
    const flag = (item as any).flag_renovacao ?? (item as any).flRenovacao ?? (item as any).flagRenovacao;
    if (flag === 'S') return true;
    if (flag === 'N') return false;
    return null;
  }

  onSortChange(sort: { field: string, order: 'ASC' | 'DESC' }): void {
    this.currentSortField = sort.field;
    this.currentSortOrder = sort.order;
    this.loadData(1, this.serverSidePagination?.pageSize || 20);
  }

  onSearchChange(searchText: string): void {
    this.currentSearchText = searchText;
    this.loadData();
  }

  onFilterChange(filters: string[]): void {
    this.currentFilters = filters;
    this.loadData();
  }

  onClearAllFilters(): void {
    this.currentSearchText = '';
    this.currentFilters = [];
    this.currentDateRange = { start: '', end: '' };
    this.currentSortField = '';
    this.currentSortOrder = 'DESC';

    this.hasReceivedInitialDates = true;

    this.loadData();
  }

  private parseDate(dateString: string): Date | undefined {
    if (!dateString || dateString.trim() === '') {
      return undefined;
    }

    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    if (dateString.includes('-')) {
      return new Date(dateString);
    }

    return undefined;
  }

  onPageSizeChange(newPageSize: number): void {
    this.loadData(1, newPageSize);
  }

  onPageChange(newPage: number): void {
    if (this.serverSidePagination) {
      this.loadData(newPage, this.serverSidePagination.pageSize);
    }
  }

  onViewLead(lead: LeadRow): void {
    if (lead.idLead) {
      this.router.navigate([`/leads/${lead.idLead}/detalhes`]);
    }
  }
}
